import React, { useEffect, useRef, useState } from 'react';

import { Icon, Icons, Label, ListItem, Radio } from '@silverfort/sf-components';

import styles from './ResourceDetails.module.css';
import { allComputersResource } from '../../../Policy/common';

const PROTECTED_SERVICES_OPTIONS = {
    ANY: 'Any',
    EXCLUDE: 'Exclude',
    INCLUDE: 'Include'
};

const MAIN_SERVICES = {
    cifs: 'The service principal name CIFS is commonly used for native file-sharing in Windows.',
    host: 'The service principal name HOST is used to access the host computer account.',
    http: 'The service principal name HTTP is common in Kerberos authentication to web servers. This is also the SPN that is requested when users execute remote Powershell for example.',
    krbtgt: 'The AD authentication service, which grants ticket granting tickets (TGTs).   TGTs are basic tickets used for all Kerberos authentication. Without a TGT, the user will not be able to access any Kerberos service.',
    ldap: 'The service principal name LDAP is of interest when using Kerberos to authenticate to a resource that can speak LDAP.',
    rpc: '',
    rpcss: 'The service principal name RPCSS is used for various Windows Remote Server Administration Tools.',
    termsrv: 'The service principal name TERMSRV is used for remote access when using RDP.'
};

const mainServicesNames = Object.keys(MAIN_SERVICES);

const getDefaultServiceType = (focusedResource) => {
    return focusedResource.match_all_services
        ? focusedResource.selectedServices?.length
            ? PROTECTED_SERVICES_OPTIONS.EXCLUDE
            : PROTECTED_SERVICES_OPTIONS.ANY
        : PROTECTED_SERVICES_OPTIONS.INCLUDE;
};

const ServiceRow = ({ service, isSelected, onSelectService }) => {
    const shortServiceName = service.split('/')[0];
    const tooltipInfo = MAIN_SERVICES[shortServiceName] || false;

    return (
        <ListItem
            title={<Label text={service} tooltipInfo={tooltipInfo} />}
            multiSelect
            selected={isSelected}
            onCheck={onSelectService}
            value={service}
        />
    );
};

const ServicesTypesSelection = ({ type, onTypeChange }) => {
    return (
        <div className="flex">
            {Object.values(PROTECTED_SERVICES_OPTIONS).map((option) => {
                return (
                    <div className="flex items-center mr-6" onClick={() => onTypeChange(option)}>
                        <Radio id={option} checked={type === option} className="mr-2" />
                        <Label text={option} />
                    </div>
                );
            })}
        </div>
    );
};

const ResourceDetails = ({
    focusedResource,
    setFocusedResource,
    resourceIcon,
    selectServicesToAllResources,
    setApplyAllMsg,
    isEditor
}) => {
    const [focusedResourceName, setFocusedResourceName] = useState('');
    const [showAddService, setShowAddService] = useState(false);
    const [newServiceName, setNewServiceName] = useState('');
    const [isNewServiceExists, setIsNewServiceExists] = useState(false);
    const [showAllServices, setShowAllServices] = useState(false);
    const [addServiceErrMsg, setAddServiceErrMsg] = useState('');
    const [servicesType, setServicesType] = useState(getDefaultServiceType(focusedResource));

    const inputRef = useRef(null);

    useEffect(() => {
        setServicesType(getDefaultServiceType(focusedResource));
    }, [focusedResource?.guid]);

    useEffect(() => {
        const isAlreadyExists = focusedResource.services && focusedResource.services.includes(newServiceName);
        setIsNewServiceExists(isAlreadyExists);
        if (!isAlreadyExists && addServiceErrMsg) {
            setAddServiceErrMsg('');
        }

        if (focusedResourceName !== focusedResource.display_name || focusedResource.name) {
            setFocusedResourceName(focusedResource.display_name || focusedResource.name);
        }
    }, [focusedResource, newServiceName, focusedResourceName, addServiceErrMsg]);

    useEffect(() => {
        setShowAllServices(false);
    }, [focusedResourceName]);

    useEffect(() => {
        if (showAddService && inputRef?.current) {
            inputRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [showAddService]);

    const onSelectService = (service) => {
        const newFocusedResource = { ...focusedResource };
        let itemIndex = -1;
        const isSelected = newFocusedResource.selectedServices.some(function (selectedService, index) {
            itemIndex = index;
            return service === selectedService.service_identifier;
        });

        if (isSelected) {
            newFocusedResource.selectedServices.splice(itemIndex, 1);
        } else {
            newFocusedResource.selectedServices.push({
                service_identifier: service,
                type: 1
            });
        }

        setFocusedResource(newFocusedResource);
    };

    const clearAllServices = () => {
        setFocusedResource({ ...focusedResource, selectedServices: [] });
    };

    const selectAllServices = () => {
        const newFocusedResource = { ...focusedResource };

        newFocusedResource.selectedServices = newFocusedResource.services.map((service) => {
            return {
                service_identifier: service,
                type: 1
            };
        });
        setFocusedResource(newFocusedResource);
    };

    const addService = () => {
        if (newServiceName === '' || isNewServiceExists) {
            setAddServiceErrMsg(
                isNewServiceExists
                    ? 'Service already exists'
                    : newServiceName === ''
                    ? 'Service name cannot be empty'
                    : ''
            );
            return;
        }
        setAddServiceErrMsg('');
        const newFocusedResource = { ...focusedResource };
        newFocusedResource.services.push(newServiceName);
        newFocusedResource.selectedServices.push({
            service_identifier: newServiceName,
            type: 1,
            isCustom: true
        });
        setFocusedResource(newFocusedResource);
        setShowAddService(false);
        setNewServiceName('');
    };

    const onApplyAllClicked = () => {
        const isExclude = focusedResource.excluded;
        const results = selectServicesToAllResources(
            focusedResource.selectedServices,
            isExclude,
            focusedResource.match_all_services
        );
        if (results) {
            setApplyAllMsg({
                text: ` Selection applied to all ${isExclude ? 'excluded' : 'selected'} resources`,
                isExclude: isExclude
            });
        }
    };

    const onTypeChange = (type) => {
        const selectedServices = [];
        if (type !== PROTECTED_SERVICES_OPTIONS.ANY && focusedResource?.services?.length) {
            selectedServices.push(
                ...focusedResource.services.map((service) => ({ service_identifier: service, type: 1 }))
            );
        }
        setFocusedResource({
            ...focusedResource,
            match_all_services: type !== PROTECTED_SERVICES_OPTIONS.INCLUDE,
            selectedServices: selectedServices
        });

        setServicesType(type);
    };

    const isMainService = (service) => {
        const shortServiceName = service.split('/')[0];
        return mainServicesNames.includes(shortServiceName);
    };

    const sortServices = (services, selectedServicesIdsSet) => {
        return services.sort((s1, s2) => {
            if (selectedServicesIdsSet.has(s1) && !selectedServicesIdsSet.has(s2)) {
                return -1;
            } else if (!selectedServicesIdsSet.has(s1) && selectedServicesIdsSet.has(s2)) {
                return 1;
            } else if (isMainService(s1) && !isMainService(s2)) {
                return -1;
            } else if (isMainService(s2) && !isMainService(s1)) {
                return 1;
            } else {
                return s1.localeCompare(s2);
            }
        });
    };

    const displayedServices = [];
    const hiddenServices = [];

    if (servicesType !== PROTECTED_SERVICES_OPTIONS.ANY) {
        const selectedServicesIds = [];
        if (focusedResource.selectedServices) {
            selectedServicesIds.push(
                ...focusedResource.selectedServices.map((selectedService) => selectedService.service_identifier)
            );
        }

        const selectedServicesIdsSet = new Set(selectedServicesIds);

        const sortedServices = sortServices(focusedResource.services, selectedServicesIdsSet);

        for (const service of sortedServices) {
            const isSelected = selectedServicesIdsSet.has(service);
            const mainService = isMainService(service);

            const serviceObj = (
                <ServiceRow
                    isSelected={isSelected}
                    service={service}
                    onSelectService={() => onSelectService(service)}
                />
            );
            if (isSelected || mainService) {
                displayedServices.push(serviceObj);
            } else {
                hiddenServices.push(serviceObj);
            }
        }
    }

    const showSeeAllBtn = hiddenServices.length > 0 && !showAllServices;
    const showApplyToAllBtn = isEditor && focusedResource?.guid !== allComputersResource.guid;

    return (
        <div className="min-h-[390px]">
            <div className={`${styles.detailedResource}${!isEditor ? ' ' + styles.nonEditor : ''} flex flex-col`}>
                <div className={styles.detailedResourceHeader}>
                    <img src={resourceIcon} alt="" />
                    <div className={styles.detailedResourceName}>
                        {focusedResource.display_name || focusedResource.name}
                    </div>
                </div>
                <div className="flex flex-col px-4 gap-y-3 py-4">
                    <div className="font-sf text-14 ">
                        Protected services <span className="text-10 text-[#686A6C]">(Kerberos Only)</span>
                    </div>
                    <ServicesTypesSelection type={servicesType} onTypeChange={onTypeChange} />
                    <div className="border rounded border-sf-gray-300">
                        {isEditor && (
                            <div className="flex justify-end p-2 border-b border-sf-gray-300">
                                <div
                                    className="text-[#368fef] cursor-pointer text-[14px] ml-[0] mr-[10px] my-[0]"
                                    onClick={selectAllServices}>
                                    Select All
                                </div>

                                <div
                                    className="text-[#368fef] cursor-pointer text-[14px] ml-[0] mr-[10px] my-[0]"
                                    onClick={clearAllServices}>
                                    Clear All
                                </div>
                            </div>
                        )}
                        <div className="overflow-y-scroll h-[152px] pb-2">
                            <div className="flex items-center px-3 py-2">
                                <Icon icon={Icons.CircleInfo} className="mr-1 h-4 w-4 text-sf-gray-400" />
                                <span className="text-13 text-sf-gray-400">
                                    {servicesType === PROTECTED_SERVICES_OPTIONS.ANY
                                        ? `Any SPN + Unresolved SPN`
                                        : `Pick only SPNs you would like to `}
                                    {servicesType === PROTECTED_SERVICES_OPTIONS.EXCLUDE && (
                                        <span className="text-sf-negative-500">exclude</span>
                                    )}
                                    {servicesType === PROTECTED_SERVICES_OPTIONS.INCLUDE && (
                                        <span className="text-sf-primary-500">include</span>
                                    )}
                                </span>
                            </div>
                            {displayedServices}
                            {showAllServices && hiddenServices}
                            {showSeeAllBtn && (
                                <div
                                    className="text-13 text-sf-primary-700 px-2"
                                    onClick={() => setShowAllServices(true)}>
                                    Show more...
                                </div>
                            )}
                            {!showSeeAllBtn && showAddService && (
                                <>
                                    <div className="flex items-center">
                                        <input
                                            ref={inputRef}
                                            id="identityAppKey"
                                            type="text"
                                            className="form-control addServiceInput px-2 mx-2 min-w-[80px] flex border-0 shadow-none focus:border focus:border-sf-primary-500"
                                            value={newServiceName}
                                            onChange={(e) => setNewServiceName(e.target.value)}
                                            placeholder="Type service name"
                                        />
                                        <i className="fa fa-times" onClick={() => setShowAddService(false)}></i>
                                        <i className="fa fa-check" onClick={addService} />
                                    </div>
                                    {addServiceErrMsg && (
                                        <div className="text-red-700 text-base ml-0.5 mt-0.5">{addServiceErrMsg}</div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {isEditor && (
                        <div
                            className={`text-13 ${
                                showSeeAllBtn || servicesType === PROTECTED_SERVICES_OPTIONS.ANY
                                    ? 'text-sf-gray-300'
                                    : 'text-sf-primary-700'
                            } `}
                            onClick={() => {
                                if (!showSeeAllBtn && servicesType !== PROTECTED_SERVICES_OPTIONS.ANY) {
                                    setShowAddService(true);
                                }
                            }}>
                            + Add service
                        </div>
                    )}
                </div>
            </div>

            {showApplyToAllBtn && (
                <div className={styles.detailedResourceFooter}>
                    <div className={styles.applyAllContainer} onClick={onApplyAllClicked}>
                        <i className={`fa fa-arrow-down ${styles.footerIcon}`}></i>
                        <div className={styles.footerText}>{`Apply to all ${
                            focusedResource.excluded ? 'excluded' : 'selected'
                        } resources`}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResourceDetails;
